import React from 'react';

import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';

import { CONTEXT_LESSONS_ITEMS } from 'constants/common';

import { formatDate } from 'helpers/formatDate';

import { getTranslation } from 'helpers/getTranslation';

import { Loading } from 'components/shared/Loading';
import { Switch } from 'components/shared/Switch';
import { Select } from 'components/shared/Select';
import { Typography } from 'components/shared/Typography';
import { Button } from 'components/shared/Button';

import { MenuLayout } from 'components/Layout';
import { LessonIcon } from 'components/LessonIcon';
import { EmptyState } from 'components/EmptyState';
import { ListToolbar } from 'components/Toolbar';
import { TablePagination } from 'components/TablePagination';
import { TableToolbar } from 'components/TableToolbar';
import { Tooltip } from 'components/Tooltip';
import { CoursePreview } from 'components/CoursePreview';
import { SchoolPreview } from 'components/SchoolPreview';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import { CoursesLessonsGroupedTable } from './components/CoursesLessonsGroupedTable';
import { CoursesLessonsSchoolSelect } from './components/CoursesLessonsSchoolSelect';
import { LessonAttachCoursesDrawer } from './components/LessonAttachCoursesDrawer';
import { LessonReplaceInCourseDrawer } from './components/LessonReplaceInCourseDrawer';
import { GROUP_BY_OPTIONS } from './CoursesLessonsPage.grouping.config';
import { getLessonRowModuleTitle } from './CoursesLessonsPage.helpers';
import { coursesLessonsPerPageOptions } from './CoursesLessonsPage.pagination.config';
import type { CoursesLessonsViewMode } from './CoursesLessonsPage.hooks';

import styles from './CoursesLessonsPage.module.scss';

const cellRenderer = (
  {
    item,
    key,
    minWidth,
    maxWidth,
  }: { item: any; key: any; minWidth: any; maxWidth: any },
  DefaultCell: any,
) => {
  switch (key) {
    case 'title': {
      const lessonTypeOption = CONTEXT_LESSONS_ITEMS.find(
        (option) => option.value === item.type,
      );
      const lessonTypeLabel = lessonTypeOption?.text || item.type;
      const moduleTitle = getLessonRowModuleTitle(item);
      const lessonTypeIcon = (
        <div className={styles.iconWrapper}>
          <LessonIcon className={styles.icon} type={item.type} />
        </div>
      );

      return (
        <div
          className={styles.titleCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          {lessonTypeLabel ? (
            <Tooltip
              text={lessonTypeLabel}
              triggerClassName={styles.iconTrigger}
              triggerElement={lessonTypeIcon}
            />
          ) : (
            lessonTypeIcon
          )}
          <Typography
            className={styles.title}
            variant="body3"
            weight="medium"
            mode="compact"
          >
            {item[key] || '-'}
            {moduleTitle ? (
              <span className={styles.moduleSuffix}> ({moduleTitle})</span>
            ) : null}
          </Typography>
        </div>
      );
    }
    case 'courses': {
      if (!item.courses?.length) {
        return (
          <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
            -
          </DefaultCell>
        );
      }

      return (
        <div
          className={styles.coursesCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <div className={styles.courses}>
            {item.courses.map((course: any) => (
              <Tooltip
                key={course.id}
                text={`${course.title} (${getCourseType(course.type)})`}
                triggerClassName={styles.courseTrigger}
                triggerElement={
                  <CoursePreview
                    className={styles.coursePreview}
                    preview={course.preview}
                  />
                }
              />
            ))}
          </div>
        </div>
      );
    }
    case 'schools': {
      if (!item.schools?.length) {
        return (
          <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
            -
          </DefaultCell>
        );
      }

      return (
        <div
          className={styles.schoolsCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <div className={styles.schools}>
            {item.schools.map((school: any) => (
              <Tooltip
                key={school.id}
                text={school.name}
                triggerClassName={styles.schoolTrigger}
                triggerElement={
                  <SchoolPreview
                    className={styles.schoolPreview}
                    preview={school.preview}
                    name={school.name}
                  />
                }
              />
            ))}
          </div>
        </div>
      );
    }
    case 'createdAt':
    case 'updatedAt': {
      const dateValue = item[key];
      const formattedDate =
        dateValue && isValid(parseISO(dateValue))
          ? formatDate(dateValue, 'dd MMM, yyyy')
          : '-';

      return (
        <DefaultCell key={key} style={{ minWidth, maxWidth }}>
          {formattedDate}
        </DefaultCell>
      );
    }
    default: {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {item[key] || '-'}
        </DefaultCell>
      );
    }
  }
};

const CoursesLessonsPage = ({
  viewMode,
  isRootAdmin,
  schoolsLoading,
  coursesLoading,
  loading,
  lessons,
  groupBy,
  currentGroupByOption,
  lessonGroups,
  tableColumns,
  showGroupHeaders,
  currentPage,
  totalPages,
  perPage,
  sortParams,
  showArchived,
  schoolOptions,
  selectedSchoolOption,
  courseOptions,
  selectedCourseOption,
  onSearchChange,
  onShowArchivedChange,
  onGroupByChange,
  onSchoolChange,
  onCourseChange,
  onChangeCourseClick,
  onSortChange,
  onPerPageChange,
  onNextPageClick,
  onPrevPageClick,
  onLessonRowClick,
  onLessonEditClick,
  onLessonRemoveClick,
  onLessonAttachClick,
  onLessonReplaceClick,
  attachDrawerOpen,
  selectedLessonForAttach,
  replaceDrawerOpen,
  selectedLessonForReplace,
  onAttachDrawerClose,
  onReplaceDrawerClose,
  onAttachmentsChange,
}: {
  viewMode: CoursesLessonsViewMode;
  isRootAdmin: boolean;
  schoolsLoading: boolean;
  coursesLoading: boolean;
  loading: boolean;
  lessons: any;
  groupBy: any;
  currentGroupByOption: any;
  lessonGroups: any;
  tableColumns: any;
  showGroupHeaders: any;
  currentPage: any;
  totalPages: any;
  perPage: any;
  sortParams: any;
  showArchived: any;
  schoolOptions: any;
  selectedSchoolOption: any;
  courseOptions: any;
  selectedCourseOption: any;
  onSearchChange: any;
  onShowArchivedChange: any;
  onGroupByChange: any;
  onSchoolChange: any;
  onCourseChange: any;
  onChangeCourseClick: any;
  onSortChange: any;
  onPerPageChange: any;
  onNextPageClick: any;
  onPrevPageClick: any;
  onLessonRowClick: any;
  onLessonEditClick: any;
  onLessonRemoveClick: any;
  onLessonAttachClick: any;
  onLessonReplaceClick: any;
  attachDrawerOpen: any;
  selectedLessonForAttach: any;
  replaceDrawerOpen: any;
  selectedLessonForReplace: any;
  onAttachDrawerClose: any;
  onReplaceDrawerClose: any;
  onAttachmentsChange: any;
}) => {
  const tableActions = [
    {
      key: 'edit',
      icon: 'edit',
      tooltipText: getTranslation('courses_lessons_page.actions.edit'),
      onClick: onLessonEditClick,
    },
    {
      key: 'remove',
      icon: 'trash',
      tooltipText: getTranslation('courses_lessons_page.actions.remove'),
      onClick: onLessonRemoveClick,
    },
    {
      key: 'attach',
      icon: 'link',
      tooltipText: getTranslation('courses_lessons_page.actions.attach'),
      onClick: onLessonAttachClick,
    },
    {
      key: 'replace',
      icon: 'resync',
      tooltipText: getTranslation('courses_lessons_page.actions.replace'),
      onClick: onLessonReplaceClick,
    },
  ];

  const renderSelectionPanel = () => {
    if (schoolsLoading || (coursesLoading && !courseOptions.length)) {
      return <Loading />;
    }

    if (isRootAdmin && !schoolOptions.length) {
      return (
        <EmptyState
          className={styles.emptyState}
          icon="schools"
          title={getTranslation('courses_lessons_page.selection.empty_schools.title')}
          text={getTranslation('courses_lessons_page.selection.empty_schools.text')}
        />
      );
    }

    if (selectedSchoolOption && !coursesLoading && !courseOptions.length) {
      return (
        <EmptyState
          className={styles.emptyState}
          icon="courses"
          title={getTranslation('courses_lessons_page.selection.empty_courses.title')}
          text={getTranslation('courses_lessons_page.selection.empty_courses.text')}
        />
      );
    }

    return (
      <div className={styles.selectionPanel}>
        {isRootAdmin ? (
          <div className={styles.selectionField}>
            <Typography
              className={styles.selectionLabel}
              variant="body2"
              weight="medium"
              mode="compact"
            >
              {getTranslation('courses_lessons_page.selection.school_label')}
            </Typography>
            <CoursesLessonsSchoolSelect
              className={styles.selectionSelect}
              options={schoolOptions}
              value={selectedSchoolOption}
              placeholder={getTranslation(
                'courses_lessons_page.selection.school_placeholder',
              )}
              onChange={onSchoolChange}
            />
          </div>
        ) : null}
        <div className={styles.selectionField}>
          <Typography
            className={styles.selectionLabel}
            variant="body2"
            weight="medium"
            mode="compact"
          >
            {getTranslation('courses_lessons_page.selection.course_label')}
          </Typography>
          <Select
            className={styles.selectionSelect}
            searchable
            hideSelectedOptions={false}
            loading={coursesLoading}
            disabled={!selectedSchoolOption}
            options={courseOptions}
            value={selectedCourseOption}
            placeholder={getTranslation(
              'courses_lessons_page.selection.course_placeholder',
            )}
            onChange={onCourseChange}
          />
        </div>
      </div>
    );
  };

  const renderLessonsContent = () => {
    if (loading) {
      return <Loading />;
    }

    if (lessons.length) {
      return (
        <div className={styles.inner}>
          <div className={styles.tableToolbar}>
            <Button
              className={styles.changeCourseButton}
              variant="outlined"
              size="sm"
              onClick={onChangeCourseClick}
            >
              {getTranslation('courses_lessons_page.selection.change_course')}
            </Button>
            <div className={styles.tableToolbarSearch}>
              <TableToolbar
                className={styles.tableToolbarSearchInner}
                onSearchChange={onSearchChange}
              />
            </div>
            <div className={styles.groupBy}>
              <Typography
                className={styles.groupByText}
                variant="body2"
                weight="medium"
                mode="compact"
              >
                {getTranslation('courses_lessons_page.group_by.label')}
              </Typography>
              <Select
                className={styles.groupBySelect}
                hideSelectedOptions={false}
                options={GROUP_BY_OPTIONS}
                value={currentGroupByOption}
                onChange={onGroupByChange}
              />
            </div>
          </div>
          <CoursesLessonsGroupedTable
            groupBy={groupBy}
            columns={tableColumns}
            params={sortParams}
            lessonGroups={lessonGroups}
            showGroupHeaders={showGroupHeaders}
            actions={tableActions}
            cellRenderer={(props: any, DefaultCell: any) =>
              cellRenderer(props, DefaultCell)
            }
            onSortChange={onSortChange}
            onRowClick={onLessonRowClick}
          />
          <TablePagination
            currentPage={currentPage}
            perPage={perPage}
            totalPages={totalPages}
            perPageOptions={coursesLessonsPerPageOptions}
            onPerPageChange={onPerPageChange}
            onNextPageClick={onNextPageClick}
            onPrevPageClick={onPrevPageClick}
          />
        </div>
      );
    }

    return (
      <EmptyState
        className={styles.emptyState}
        icon="lessons"
        title={getTranslation('courses_lessons_page.selection.empty_lessons.title')}
        text={getTranslation('courses_lessons_page.selection.empty_lessons.text')}
      />
    );
  };

  return (
    <MenuLayout>
      <ListToolbar
        title="Courses Lessons"
        actionComponent={
          viewMode === 'lessons' ? (
            <div className={styles.toolbarActions}>
              <div className={styles.archivedFilter}>
                <Typography className={styles.archivedLabel} variant="body2">
                  {getTranslation('courses_page.show_archived')}
                </Typography>
                <Switch
                  size="sm"
                  checked={showArchived}
                  onChange={onShowArchivedChange}
                />
              </div>
            </div>
          ) : null
        }
      />
      {viewMode === 'selection' ? renderSelectionPanel() : renderLessonsContent()}
      <LessonAttachCoursesDrawer
        open={attachDrawerOpen}
        lesson={selectedLessonForAttach}
        onClose={onAttachDrawerClose}
        onAttachmentsChange={onAttachmentsChange}
      />
      <LessonReplaceInCourseDrawer
        open={replaceDrawerOpen}
        lesson={selectedLessonForReplace}
        onClose={onReplaceDrawerClose}
        onReplaceComplete={onAttachmentsChange}
      />
    </MenuLayout>
  );
};

export default React.memo(CoursesLessonsPage) as any;
